/**
 * TEMPORARY executory model — respond to an audit finding/paragraph.
 *
 * Staff-persona audit items are auditor-raised findings allocated to me to
 * respond to (NOT cadence). This placeholder fetches the finding (release-gated,
 * so it tolerates a 404) and offers a stub response box. Real submit/close/reopen
 * wiring (the adt /respond endpoints) lands in a later phase. Opened from the
 * Audit tab, the Plan calendar drill-down, and the Inbox chain.
 */
import React, { useEffect, useState } from "react";
import { Search } from "lucide-react";
import Modal from "../../../components/common/Modal";
import Button from "../../../components/common/Button";
import FormControl from "../../../components/common/FormControl";
import Skeleton from "../../../components/common/Skeleton";
import apiClient from "../../../services/apiService";

const AuditResponseModal: React.FC<{
    findingId: number;
    onClose: () => void;
}> = ({ findingId, onClose }) => {
    const [finding, setFinding] = useState<any | null>(null);
    const [loading, setLoading] = useState(true);
    const [gated, setGated] = useState(false);
    const [response, setResponse] = useState("");

    useEffect(() => {
        let live = true;
        (async () => {
            try {
                const res = await apiClient.get(`/api/v1/adt/findings/${findingId}`);
                if (live) setFinding(res.data);
            } catch {
                if (live) setGated(true);   // release-gated / not visible yet
            } finally {
                if (live) setLoading(false);
            }
        })();
        return () => { live = false; };
    }, [findingId]);

    const closed = finding?.is_closed === true || finding?.is_closed === 1;

    return (
        <Modal isOpen onClose={onClose} size="md" closeOnOverlayClick={false}>
            <Modal.Header>
                <Search size={16} style={{ verticalAlign: -2, marginRight: 8 }} />
                Audit Finding — respond <span className="text-muted">#{findingId}</span>
            </Modal.Header>
            <Modal.Body>
                {loading && <Skeleton.Box width="100%" height={80} />}
                {!loading && gated && (
                    <div style={{ padding: 12, background: "#fff7ed", borderRadius: 8, fontSize: 13, color: "#9a3412" }}>
                        Finding #{findingId} is not released to you yet (or details are restricted).
                        This is the temporary response model — full wiring comes later.
                    </div>
                )}
                {!loading && finding && (
                    <>
                        <div style={{ marginBottom: 12 }}>
                            <span className={`status-badge sm ${closed ? "success" : "warning"}`}>
                                {closed ? "Closed" : "Open"}
                            </span>
                        </div>
                        <dl style={{ display: "grid", gridTemplateColumns: "130px 1fr", rowGap: 8, fontSize: 13 }}>
                            <dt className="text-muted">Finding</dt><dd>{finding.findings_text || "—"}</dd>
                            <dt className="text-muted">Procedure</dt><dd>{finding.procedure_name || "—"}</dd>
                            <dt className="text-muted">Program</dt><dd>{finding.program_name || finding.program_code || "—"}</dd>
                            <dt className="text-muted">Response due</dt><dd>{finding.allocation_due_date || "—"}</dd>
                            {finding.response_text && (<><dt className="text-muted">Prior response</dt><dd>{finding.response_text}</dd></>)}
                        </dl>
                    </>
                )}
                <div style={{ marginTop: 16 }}>
                    <label className="text-muted" style={{ fontSize: 12 }}>Your response</label>
                    <FormControl.Textarea
                        value={response}
                        onChange={(e: any) => setResponse(e.target.value)}
                        placeholder="Draft your response to this finding…"
                        rows={4}
                    />
                </div>
                <div style={{ marginTop: 12, padding: 12, background: "#f8fafc", borderRadius: 8, fontSize: 12, color: "#64748b" }}>
                    Temporary model. Submit / close / reopen will be wired to the audit
                    response endpoints in a later phase.
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="update" disabled title="Response submission lands in a later phase">
                    Submit Response
                </Button>
                <Button variant="fill" onClick={onClose}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AuditResponseModal;
